import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import closeIcon from 'assets/close-modal.svg';

import { getTemplateDisplayName } from 'Pages/TeamDashboard/CreateReview/components/constants';

import { formatDate } from 'utlis/string_format';

import styles from './styles.module.scss';

const FIREBASE_PREFIX = process.env.REACT_APP_FIREBASE_STORAGE_PREFIX;

const getAnalyticsAttachments = extraData => {
  const raw = extraData?.analytics_attachments || extraData?.analyticsAttachments;

  if (!raw) {
    return [];
  }

  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);

      return Array.isArray(parsed) ? parsed : [];
    }
    catch {
      return [];
    }
  }

  return Array.isArray(raw) ? raw : [];
};

const buildAttachmentUrl = url => {
  if (!url) {
    return null;
  }

  if (url.startsWith('http')) {
    return url;
  }

  return `${FIREBASE_PREFIX}${encodeURIComponent(url)}?alt=media`;
};

const getAttachmentFileName = attachment => {
  if (attachment?.name) {
    return attachment.name;
  }

  const url = attachment?.url;

  if (!url) {
    return 'Uploaded file';
  }

  try {
    const path = decodeURIComponent(url.split('?')[0]);

    return path.split('/').pop() || 'Uploaded file';
  }
  catch {
    return 'Uploaded file';
  }
};

const getFeedbackItemsCount = reviewData => {
  const extraData = reviewData?.extra_data;
  const deeperInsights = extraData?.post_processes?.deeper_insights;
  const hasExtraDataInsights = deeperInsights?.status === 2
    && deeperInsights?.feedback?.length > 0;

  const rawFeedback = reviewData?.final_feedback || [];
  const filteredFeedback = hasExtraDataInsights
    ? rawFeedback.filter(item => item.category !== 'deeper_insight' && !item.isDeeperInsight)
    : rawFeedback;

  if (hasExtraDataInsights) {
    return filteredFeedback.length + deeperInsights.feedback.length;
  }

  return filteredFeedback.length;
};

const getAuditMeta = reviewData => {
  const extraData = reviewData?.extra_data;
  const reviewType = extraData?.template ?? reviewData?.template;
  const totalFindings = getFeedbackItemsCount(reviewData);
  const templateDisplayName = getTemplateDisplayName(reviewType);

  return {
    findingsLabel: `${totalFindings} finding${totalFindings === 1 ? '' : 's'}`,
    templateDisplayName,
  };
};

const ContextModal = ({
  isOpen,
  onClose,
  reviewData,
  productDescription,
  audience,
  businessGoal,
  isReviewComplete,
  isGeneratingPdf,
  onDownloadPdf,
}) => {
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimerRef = useRef(null);

  const extraData = reviewData?.extra_data;
  const auditMeta = useMemo(() => getAuditMeta(reviewData), [reviewData]);
  const analyticsAttachments = useMemo(() => getAnalyticsAttachments(extraData), [extraData]);

  const displayAudience = extraData?.audience || audience || 'No audience provided';
  const displayProblem = extraData?.user_problem || productDescription || 'No product description provided';
  const displayBusinessGoal = extraData?.business_goal || businessGoal || 'No business goal provided';
  const displayObservedAnalytics = extraData?.observed_analytics || '';
  const personaName = extraData?.persona_name;

  const hasAnalytics = Boolean(displayObservedAnalytics) || analyticsAttachments.length > 0;

  const handleScroll = useCallback(() => {
    setIsScrolling(true);
    clearTimeout(scrollTimerRef.current);
    scrollTimerRef.current = setTimeout(() => setIsScrolling(false), 1000);
  }, []);

  useEffect(() => () => clearTimeout(scrollTimerRef.current), []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className={styles.overlay}>
      <div className={styles.backdrop} onClick={onClose} />
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="audit-context-heading"
        onClick={event => event.stopPropagation()}
      >
        <div className={styles.header}>
          <div className={styles['header-left']}>
            <h2 id="audit-context-heading" className={styles.heading}>
              Audit context
            </h2>
            <div className={styles.meta}>
              <span className={styles['meta-item']}>
                {auditMeta.findingsLabel}
              </span>
              {auditMeta.templateDisplayName && (
                <>
                  <span className={styles['meta-item']}>
                    |
                  </span>
                  <span className={styles['meta-item']}>
                    {auditMeta.templateDisplayName}
                  </span>
                </>
              )}
            </div>
          </div>
          <button
            type="button"
            className={styles['close-button']}
            onClick={onClose}
            aria-label="Close"
          >
            <img src={closeIcon} alt="" />
          </button>
        </div>

        <div
          className={cn(styles.content, { [styles['is-scrolling']]: isScrolling })}
          onScroll={handleScroll}
        >
          <div className={styles.fields}>
            <div>
              <div className={styles['field-label']}>
                Audience
              </div>
              <div className={styles['field-value']}>
                {personaName && (
                  <div className={styles['field-persona-name']}>
                    {personaName}
                  </div>
                )}
                {displayAudience}
              </div>
            </div>

            <div>
              <div className={styles['field-label']}>
                Problem to solve
              </div>
              <div className={styles['field-value']}>
                {displayProblem}
              </div>
            </div>

            {hasAnalytics && (
              <div>
                <div className={styles['field-label']}>
                  Analytics
                </div>
                <div className={styles['analytics-section']}>
                  {displayObservedAnalytics && (
                    <div className={styles['analytics-text']}>
                      <div className={styles['field-value']}>
                        {displayObservedAnalytics}
                      </div>
                    </div>
                  )}
                  {analyticsAttachments.map((attachment, index) => {
                    const previewUrl = buildAttachmentUrl(attachment.url);
                    const fileName = getAttachmentFileName(attachment);

                    return (
                      <div key={attachment.url || index} className={styles['analytics-file']}>
                        {previewUrl && (
                          <img src={previewUrl} alt={fileName} />
                        )}
                        <span className={styles['analytics-file-name']}>
                          {fileName}
                        </span>
                        <span className={styles['analytics-file-label']}>
                          Uploaded file
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <div className={styles['field-label']}>
                Business goal
              </div>
              <div className={styles['field-value']}>
                {displayBusinessGoal}
              </div>
            </div>
          </div>
        </div>

        <div className={cn(styles.footer, 'flex items-center justify-between')}>
          <div className="flex flex-col gap-8 text-xs">
            <span className="text-gray-500 h-4">
              Last updated
            </span>
            <span className="text-gray-900 h-4">
              {reviewData?.updated_at ? formatDate(reviewData?.updated_at) : 'Just now'}
            </span>
          </div>

          {isReviewComplete && (
            <button
              type="button"
              onClick={onDownloadPdf}
              disabled={isGeneratingPdf}
              className="flex items-center gap-8 bg-white border border-[#E1E4E8]
              rounded-lg px-5 py-2.5 shadow-[0px_2px_4px_-1px_rgba(0,0,0,0.04),0px_1px_1px_rgba(0,0,0,0.02)]
              hover:bg-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isGeneratingPdf
                ? (
                  <svg className="animate-spin w-4 h-4 text-gray-500" viewBox="0 0 24 24" fill="none">
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeDasharray="31.4 31.4"
                    />
                  </svg>
                )
                : (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M8 1v9m0 0L5 7m3 3l3-3M2 11v2a2 2 0 002 2h8a2 2 0 002-2v-2"
                      stroke="#606776"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              <span className="font-medium text-sm leading-5 text-gray-700">
                {isGeneratingPdf ? 'Generating...' : 'Download audit'}
              </span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

ContextModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  reviewData: PropTypes.object,
  productDescription: PropTypes.string,
  audience: PropTypes.string,
  businessGoal: PropTypes.string,
  isReviewComplete: PropTypes.bool,
  isGeneratingPdf: PropTypes.bool,
  onDownloadPdf: PropTypes.func,
};

ContextModal.defaultProps = {
  reviewData: null,
  productDescription: '',
  audience: '',
  businessGoal: '',
  isReviewComplete: false,
  isGeneratingPdf: false,
  onDownloadPdf: () => {},
};

export default ContextModal;
